import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { suppliersAPI, productsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Truck, Package, MoreVertical, Pencil, Trash2, ShoppingCart, CheckCircle, Loader2 } from 'lucide-react';

export default function Suppliers() {
    const { user } = useAuth();
    const [suppliers, setSuppliers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
    const [orderDialogOpen, setOrderDialogOpen] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [saving, setSaving] = useState(false);

    const [supplierForm, setSupplierForm] = useState({
        name: '',
        contact_name: '',
        email: '',
        phone: '',
        address: ''
    });

    const [orderForm, setOrderForm] = useState({
        supplier_id: '',
        items: [],
        notes: ''
    });

    const [orderItem, setOrderItem] = useState({ product_id: '', quantity: '', unit_cost: '' });

    useEffect(() => {
        if (user?.store_id) fetchData();
        else setLoading(false);
    }, [user]);

    const fetchData = async () => {
        try {
            const [suppliersRes, ordersRes, productsRes] = await Promise.all([
                suppliersAPI.getAll(),
                suppliersAPI.getOrders(),
                productsAPI.getAll()
            ]);
            setSuppliers(suppliersRes.data);
            setOrders(ordersRes.data);
            setProducts(productsRes.data);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const resetSupplierForm = () => {
        setSupplierForm({ name: '', contact_name: '', email: '', phone: '', address: '' });
        setEditingSupplier(null);
    };

    const handleOpenSupplierDialog = (supplier = null) => {
        if (supplier) {
            setEditingSupplier(supplier);
            setSupplierForm({
                name: supplier.name,
                contact_name: supplier.contact_name,
                email: supplier.email,
                phone: supplier.phone,
                address: supplier.address
            });
        } else {
            resetSupplierForm();
        }
        setSupplierDialogOpen(true);
    };

    const handleSaveSupplier = async () => {
        if (!supplierForm.name) {
            toast.error('Supplier name is required');
            return;
        }

        setSaving(true);
        try {
            if (editingSupplier) {
                await suppliersAPI.update(editingSupplier.id, supplierForm);
                toast.success('Supplier updated');
            } else {
                await suppliersAPI.create(supplierForm);
                toast.success('Supplier created');
            }
            setSupplierDialogOpen(false);
            resetSupplierForm();
            fetchData();
        } catch (error) {
            toast.error('Failed to save supplier');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteSupplier = async (id) => {
        if (!window.confirm('Delete this supplier?')) return;
        try {
            await suppliersAPI.delete(id);
            toast.success('Supplier deleted');
            fetchData();
        } catch (error) {
            toast.error('Failed to delete supplier');
        }
    };

    const handleAddOrderItem = () => {
        if (!orderItem.product_id || !orderItem.quantity || !orderItem.unit_cost) {
            toast.error('Please fill all item fields');
            return;
        }
        const product = products.find(p => p.id === orderItem.product_id);
        setOrderForm({
            ...orderForm,
            items: [...orderForm.items, {
                product_id: orderItem.product_id,
                product_name: product?.name,
                quantity: parseInt(orderItem.quantity),
                unit_cost: parseFloat(orderItem.unit_cost)
            }]
        });
        setOrderItem({ product_id: '', quantity: '', unit_cost: '' });
    };

    const handleRemoveOrderItem = (index) => {
        setOrderForm({
            ...orderForm,
            items: orderForm.items.filter((_, i) => i !== index)
        });
    };

    const handleCreateOrder = async () => {
        if (!orderForm.supplier_id || orderForm.items.length === 0) {
            toast.error('Please select a supplier and add items');
            return;
        }

        setSaving(true);
        try {
            await suppliersAPI.createOrder(orderForm);
            toast.success('Purchase order created');
            setOrderDialogOpen(false);
            setOrderForm({ supplier_id: '', items: [], notes: '' });
            fetchData();
        } catch (error) {
            toast.error('Failed to create order');
        } finally {
            setSaving(false);
        }
    };

    const handleReceiveOrder = async (orderId) => {
        try {
            await suppliersAPI.receiveOrder(orderId);
            toast.success('Order received and stock updated');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to receive order');
        }
    };

    const orderTotal = orderForm.items.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0);

    if (loading) {
        return (
            <Layout title="Suppliers">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Suppliers">
            <div className="space-y-6" data-testid="suppliers-page">
                <Tabs defaultValue="suppliers" className="space-y-4">
                    <div className="flex justify-between items-center">
                        <TabsList>
                            <TabsTrigger value="suppliers" data-testid="suppliers-tab">
                                <Truck className="h-4 w-4 mr-2" />
                                Suppliers
                            </TabsTrigger>
                            <TabsTrigger value="orders" data-testid="orders-tab">
                                <ShoppingCart className="h-4 w-4 mr-2" />
                                Purchase Orders
                            </TabsTrigger>
                        </TabsList>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setOrderDialogOpen(true)} data-testid="new-order-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                New Order
                            </Button>
                            <Button onClick={() => handleOpenSupplierDialog()} data-testid="add-supplier-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                Add Supplier
                            </Button>
                        </div>
                    </div>

                    <TabsContent value="suppliers">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {suppliers.map((supplier) => (
                                <Card key={supplier.id} className="card-hover" data-testid={`supplier-card-${supplier.id}`}>
                                    <CardHeader className="flex flex-row items-start justify-between pb-2">
                                        <div>
                                            <CardTitle className="text-lg">{supplier.name}</CardTitle>
                                            <p className="text-sm text-muted-foreground">{supplier.contact_name}</p>
                                        </div>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon">
                                                    <MoreVertical className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => handleOpenSupplierDialog(supplier)}>
                                                    <Pencil className="h-4 w-4 mr-2" /> Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => handleDeleteSupplier(supplier.id)} className="text-destructive">
                                                    <Trash2 className="h-4 w-4 mr-2" /> Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </CardHeader>
                                    <CardContent className="space-y-1 text-sm">
                                        {supplier.email && <p className="text-muted-foreground">{supplier.email}</p>}
                                        {supplier.phone && <p className="text-muted-foreground">{supplier.phone}</p>}
                                        {supplier.address && <p className="text-muted-foreground truncate">{supplier.address}</p>}
                                    </CardContent>
                                </Card>
                            ))}
                            {suppliers.length === 0 && (
                                <div className="col-span-full text-center py-12 text-muted-foreground">
                                    <Truck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                    <p>No suppliers yet</p>
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="orders">
                        <Card>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Supplier</TableHead>
                                            <TableHead>Items</TableHead>
                                            <TableHead className="text-right">Total</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead className="w-[100px]"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {orders.map((order) => (
                                            <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                                                <TableCell className="text-muted-foreground">
                                                    {new Date(order.created_at).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell className="font-medium">{order.supplier_name}</TableCell>
                                                <TableCell>{order.items.length} items</TableCell>
                                                <TableCell className="text-right font-mono">
                                                    ${order.total_amount.toFixed(2)}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={order.status === 'received' ? 'default' : 'secondary'}>
                                                        {order.status}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {order.auto_generated ? (
                                                        <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                                                            Auto
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground">Manual</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {order.status === 'pending' && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleReceiveOrder(order.id)}
                                                            data-testid={`receive-order-${order.id}`}
                                                        >
                                                            <CheckCircle className="h-4 w-4 mr-1" />
                                                            Receive
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {orders.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                                    No purchase orders
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Supplier Dialog */}
            <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
                <DialogContent data-testid="supplier-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="s-name">Name *</Label>
                            <Input
                                id="s-name"
                                value={supplierForm.name}
                                onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                                data-testid="supplier-name-input"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="s-contact">Contact Name</Label>
                                <Input
                                    id="s-contact"
                                    value={supplierForm.contact_name}
                                    onChange={(e) => setSupplierForm({ ...supplierForm, contact_name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="s-email">Email</Label>
                                <Input
                                    id="s-email"
                                    type="email"
                                    value={supplierForm.email}
                                    onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="s-phone">Phone</Label>
                                <Input
                                    id="s-phone"
                                    value={supplierForm.phone}
                                    onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="s-address">Address</Label>
                                <Input
                                    id="s-address"
                                    value={supplierForm.address}
                                    onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSupplierDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSaveSupplier} disabled={saving} data-testid="save-supplier-btn">
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {editingSupplier ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Order Dialog */}
            <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
                <DialogContent className="max-w-2xl" data-testid="order-dialog">
                    <DialogHeader>
                        <DialogTitle>Create Purchase Order</DialogTitle>
                        <DialogDescription>Order products from a supplier</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Supplier *</Label>
                            <Select value={orderForm.supplier_id} onValueChange={(v) => setOrderForm({ ...orderForm, supplier_id: v })}>
                                <SelectTrigger data-testid="order-supplier-select">
                                    <SelectValue placeholder="Select supplier" />
                                </SelectTrigger>
                                <SelectContent>
                                    {suppliers.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Add Items</Label>
                            <div className="grid grid-cols-4 gap-2">
                                <Select value={orderItem.product_id} onValueChange={(v) => setOrderItem({ ...orderItem, product_id: v })}>
                                    <SelectTrigger className="col-span-2" data-testid="order-product-select">
                                        <SelectValue placeholder="Product" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {products.map((p) => (
                                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Input
                                    type="number"
                                    placeholder="Qty"
                                    value={orderItem.quantity}
                                    onChange={(e) => setOrderItem({ ...orderItem, quantity: e.target.value })}
                                    data-testid="order-qty-input"
                                />
                                <div className="flex gap-2">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        placeholder="Cost"
                                        value={orderItem.unit_cost}
                                        onChange={(e) => setOrderItem({ ...orderItem, unit_cost: e.target.value })}
                                        data-testid="order-cost-input"
                                    />
                                    <Button onClick={handleAddOrderItem} data-testid="add-order-item-btn">
                                        <Plus className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {orderForm.items.length > 0 && (
                            <ScrollArea className="h-40 border rounded-md">
                                <div className="p-2 space-y-2">
                                    {orderForm.items.map((item, idx) => (
                                        <div key={idx} className="flex items-center justify-between bg-secondary/50 p-2 rounded">
                                            <span className="font-medium">{item.product_name}</span>
                                            <div className="flex items-center gap-4">
                                                <span className="font-mono text-sm">
                                                    {item.quantity} × ${item.unit_cost.toFixed(2)}
                                                </span>
                                                <span className="font-mono font-bold">
                                                    ${(item.quantity * item.unit_cost).toFixed(2)}
                                                </span>
                                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleRemoveOrderItem(idx)}>
                                                    <Trash2 className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        )}

                        {orderForm.items.length > 0 && (
                            <div className="flex justify-end">
                                <p className="font-bold">Total: <span className="font-mono">${orderTotal.toFixed(2)}</span></p>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="notes">Notes</Label>
                            <Input
                                id="notes"
                                value={orderForm.notes}
                                onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
                                placeholder="Optional notes"
                                data-testid="order-notes-input"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleCreateOrder} disabled={saving} data-testid="create-order-btn">
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Create Order
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
